/**
 * # cortex:std:parameterHandler
 * Builds a query string based on the instance's current appended value.
 *
 * ### Code example
 *
 * ```ts
 * const parameterHandler = new ParameterHandler();
 *
 * parameterHandler.append({ first: 1, second: 2 });
 * // '?first=1&second=2'
 * parameterHandler.getString();
 * ```
 */
// Reason: Needed to allow generic behavior.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ParameterHandler<T extends Record<string, any> = Record<string, any>> {
	/**
	 * Map of current parameters.
	 * */
	private parameters: T = Object.create(null);

	/** Appends a new object of parameters */
	public append(item: T): void {
		Object.assign(this.parameters, item);
	}

	/** Clears the parameter maps. */
	public clearParameters(): void {
		this.parameters = Object.create(null);
	}

	/** Returns a query string based on the values in the map. */
	public getString(): string {
		if (Object.keys(this.parameters).length === 0) return '';

		const entries = Object.entries(this.parameters);

		return entries.reduce((accumulator, currentValue, currentIndex) => {
			let combine = accumulator + currentValue.join('=');

			if (entries.length - 1 !== currentIndex) {
				combine += '&';
			}

			return combine;
		}, '?');
	}
}
